<script lang="ts">
	import { forgotPassword, resetPassword, verifyForgotPasswordOtp } from '$lib/stores/user';
	import { createEventDispatcher } from 'svelte';
	import VerificationCard from './VerificationCard.svelte';

	export let isOpen: boolean = false;
	export let onClose = () => {};
	export let onSuccess = () => {};

	const dispatch = createEventDispatcher();

	let step: 'email' | 'otp' | 'reset' = 'email';
	let email = '';
	let otpCode = ''; // Store OTP code from VerificationCard
	let newPassword = '';
	let confirmPassword = '';
	let showPassword = false;
	let showConfirmPassword = false;
	let isLoading = false;
	let errorMessage = '';
	let successMessage = '';
	let mouseDownInsideModal = false;
	let isVerificationOpen = false;

	function handleClose() {
		resetForm();
		onClose();
		dispatch('close');
	}

	function resetForm() {
		step = 'email';
		email = '';
		otpCode = '';
		newPassword = '';
		confirmPassword = '';
		errorMessage = '';
		successMessage = '';
		isLoading = false;
		isVerificationOpen = false;
	}

	function handleOverlayClick() {
		if (!mouseDownInsideModal) {
			handleClose();
		}
	}

	function handleMouseDown() {
		mouseDownInsideModal = true;
		setTimeout(() => {
			mouseDownInsideModal = false;
		}, 100);
	}

	async function handleRequestOtp() {
		if (!email) {
			errorMessage = 'Please enter your email';
			return;
		}

		isLoading = true;
		errorMessage = '';
		successMessage = '';

		try {
			await forgotPassword(email);
			successMessage = 'OTP has been sent to your email';
			setTimeout(() => {
				step = 'otp';
				isVerificationOpen = true;
				successMessage = '';
			}, 1500);
		} catch (error: any) {
			errorMessage = error.response?.data?.message || 'Failed to send OTP';
		} finally {
			isLoading = false;
		}
	}

	// Handle OTP verification success from VerificationCard
	function handleOtpVerifySuccess(result: any) {
		// VerificationCard will verify OTP via verifyFunction and return { code, email, ... }
		// The code is guaranteed to be in the result object
		if (result && result.code) {
			otpCode = result.code;
			step = 'reset';
			isVerificationOpen = false;
			errorMessage = '';
			successMessage = '';
		} else {
			// If code is not in result, something went wrong
			errorMessage = 'Failed to get OTP code. Please try again.';
		}
	}

	// Handle back from VerificationCard
	function handleOtpBack() {
		step = 'email';
		isVerificationOpen = false;
		otpCode = '';
		errorMessage = '';
		successMessage = '';
	}

	// Handle resend OTP - called when VerificationCard resends code
	function handleResendOtp() {
		// VerificationCard will handle the resend, we just need to clear any error messages
		errorMessage = '';
		successMessage = '';
	}

	async function handleResetPassword() {
		if (!newPassword || !confirmPassword) {
			errorMessage = 'Please enter your new password';
			return;
		}

		if (newPassword !== confirmPassword) {
			errorMessage = 'Passwords do not match';
			return;
		}

		if (newPassword.length < 8) {
			errorMessage = 'Password must be at least 8 characters';
			return;
		}

		if (!otpCode) {
			errorMessage = 'OTP code is required';
			return;
		}

		isLoading = true;
		errorMessage = '';
		successMessage = '';

		try {
			await resetPassword({
				email,
				otp: otpCode,
				new_password: newPassword,
				new_password_confirmation: confirmPassword
			});
			successMessage = 'Password reset successfully!';
			setTimeout(() => {
				onSuccess();
				handleClose();
			}, 1500);
		} catch (error: any) {
			const errorMsg = error.response?.data?.message || 'Failed to reset password';
			errorMessage = errorMsg;

			// If OTP is invalid or expired, go back to OTP step
			const errorMessageLower = errorMsg.toLowerCase();
			if (
				error.response?.status === 400 ||
				error.response?.status === 422 ||
				errorMessageLower.includes('otp') ||
				errorMessageLower.includes('code') ||
				errorMessageLower.includes('expired') ||
				errorMessageLower.includes('invalid')
			) {
				setTimeout(() => {
					step = 'otp';
					isVerificationOpen = true;
					newPassword = '';
					confirmPassword = '';
					otpCode = ''; // Clear OTP code to force re-entry
					errorMessage = 'OTP code is invalid or expired. Please enter a new code.';
				}, 2000);
			}
		} finally {
			isLoading = false;
		}
	}

	function handleKeyPress(event: KeyboardEvent) {
		if (event.key === 'Enter') {
			if (step === 'email') {
				handleRequestOtp();
			} else if (step === 'reset') {
				handleResetPassword();
			}
		}
	}

	function handleBack() {
		if (step === 'otp') {
			step = 'email';
			isVerificationOpen = false;
			otpCode = '';
		} else if (step === 'reset') {
			step = 'otp';
			isVerificationOpen = true;
			newPassword = '';
			confirmPassword = '';
		}
		errorMessage = '';
		successMessage = '';
	}
</script>

{#if isOpen}
	<div
		class="modal-overlay"
		on:mousedown={handleMouseDown}
		on:click={handleOverlayClick}
		on:keydown={(e) => e.key === 'Escape' && handleClose()}
		role="dialog"
		aria-modal="true"
		tabindex="-1"
	>
		<div class="modal-container" on:click|stopPropagation role="button" tabindex="0">
			<div class="modal-header">
				<button class="close-button" on:click={handleClose} aria-label="Close">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<path d="M6 6L18 18M6 18L18 6" />
					</svg>
				</button>
			</div>

			<div class="modal-content">
				{#if step === 'email'}
					<div class="icon-container">
						<svg
							width="48"
							height="48"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
						>
							<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
							<path d="M9 12l2 2 4-4" />
						</svg>
					</div>

					<h2 class="modal-title">Forgot Password</h2>
					<p class="modal-subtitle">Please enter your email to receive OTP code</p>

					<form class="form" on:submit|preventDefault={handleRequestOtp}>
						<div class="form-group">
							<input
								type="email"
								bind:value={email}
								placeholder="Email"
								class="form-input"
								on:keypress={handleKeyPress}
								disabled={isLoading}
							/>
						</div>

						{#if errorMessage}
							<div class="error-message">{errorMessage}</div>
						{/if}

						{#if successMessage}
							<div class="success-message">{successMessage}</div>
						{/if}

						<button type="submit" class="submit-button" disabled={isLoading}>
							{#if isLoading}
								<div class="spinner"></div>
								Sending...
							{:else}
								Send OTP
							{/if}
						</button>
					</form>
				{:else if step === 'otp'}
					<VerificationCard
						isOpen={isVerificationOpen}
						{email}
						autoVerify={false}
						autoClose={false}
						showModal={false}
						verifyFunction={verifyForgotPasswordOtp}
						resendFunction={forgotPassword}
						onVerifySuccess={handleOtpVerifySuccess}
						onResendCode={handleResendOtp}
						onClose={() => {
							isVerificationOpen = false;
						}}
						on:back={handleOtpBack}
						countdownSeconds={60}
					/>
				{:else if step === 'reset'}
					<div class="icon-container">
						<svg
							width="48"
							height="48"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
						>
							<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
							<path d="M9 12l2 2 4-4" />
						</svg>
					</div>

					<h2 class="modal-title">Set New Password</h2>
					<p class="modal-subtitle">Please enter your new password</p>

					<form class="form" on:submit|preventDefault={handleResetPassword}>
						<div class="form-group">
							<div class="password-input-container">
								<input
									type={showPassword ? 'text' : 'password'}
									bind:value={newPassword}
									placeholder="New Password"
									class="form-input"
									on:keypress={handleKeyPress}
									disabled={isLoading}
								/>
								<button
									type="button"
									class="password-toggle"
									on:click={() => (showPassword = !showPassword)}
								>
									{#if showPassword}
										<svg
											width="20"
											height="20"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
										>
											<path
												d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94L17.94 17.94z"
											/>
											<path d="M1 1l22 22" />
											<path
												d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19L9.9 4.24z"
											/>
										</svg>
									{:else}
										<svg
											width="20"
											height="20"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
										>
											<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
											<circle cx="12" cy="12" r="3" />
										</svg>
									{/if}
								</button>
							</div>
						</div>

						<div class="form-group">
							<div class="password-input-container">
								<input
									type={showConfirmPassword ? 'text' : 'password'}
									bind:value={confirmPassword}
									placeholder="Confirm New Password"
									class="form-input"
									on:keypress={handleKeyPress}
									disabled={isLoading}
								/>
								<button
									type="button"
									class="password-toggle"
									on:click={() => (showConfirmPassword = !showConfirmPassword)}
								>
									{#if showConfirmPassword}
										<svg
											width="20"
											height="20"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
										>
											<path
												d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94L17.94 17.94z"
											/>
											<path d="M1 1l22 22" />
											<path
												d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19L9.9 4.24z"
											/>
										</svg>
									{:else}
										<svg
											width="20"
											height="20"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
										>
											<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
											<circle cx="12" cy="12" r="3" />
										</svg>
									{/if}
								</button>
							</div>
						</div>

						{#if errorMessage}
							<div class="error-message">{errorMessage}</div>
						{/if}

						{#if successMessage}
							<div class="success-message">{successMessage}</div>
						{/if}

						<button type="submit" class="submit-button" disabled={isLoading}>
							{#if isLoading}
								<div class="spinner"></div>
								Resetting...
							{:else}
								Reset Password
							{/if}
						</button>
					</form>

					<button class="back-button" on:click={handleBack} disabled={isLoading}> Back </button>
				{/if}
			</div>
		</div>
	</div>
{/if}

<style>
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.8);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
	}

	.modal-container {
		background: #1a1a1a;
		border-radius: 16px;
		max-width: 400px;
		width: 90%;
		max-height: 90vh;
		overflow-y: auto;
		position: relative;
	}

	.modal-header {
		display: flex;
		justify-content: flex-end;
		padding: 16px;
	}

	.close-button {
		background: none;
		border: none;
		color: #ffffff;
		cursor: pointer;
		padding: 8px;
		border-radius: 50%;
		transition: background-color 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.close-button:hover {
		background: #333;
	}

	.modal-content {
		padding: 0 32px 32px 32px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 24px;
	}

	.icon-container {
		display: flex;
		justify-content: center;
		margin-bottom: 8px;
		color: #4a6cf7;
	}

	.modal-title {
		color: #ffffff;
		font-size: 20px;
		font-weight: 600;
		margin: 0;
		text-align: center;
	}

	.modal-subtitle {
		color: #888;
		font-size: 14px;
		text-align: center;
		margin: 0;
	}

	.form {
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.form-group {
		display: flex;
		flex-direction: column;
	}

	.form-input {
		background: #2a2a2a;
		border: 1px solid #404040;
		border-radius: 8px;
		padding: 12px 16px;
		color: #ffffff;
		font-size: 14px;
		transition: border-color 0.2s;
		width: 100%;
	}

	.form-input::placeholder {
		color: #888;
	}

	.form-input:focus {
		outline: none;
		border-color: #4a6cf7;
	}

	.form-input:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.password-input-container {
		position: relative;
	}

	.password-toggle {
		position: absolute;
		right: 12px;
		top: 50%;
		transform: translateY(-50%);
		background: none;
		border: none;
		color: #888;
		cursor: pointer;
		padding: 4px;
		border-radius: 4px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.password-input-container .form-input {
		padding-right: 45px;
	}

	.submit-button {
		background: #4a6cf7;
		border: none;
		border-radius: 8px;
		padding: 12px 24px;
		color: #ffffff;
		font-size: 16px;
		font-weight: 600;
		cursor: pointer;
		transition: background-color 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
		width: 100%;
	}

	.submit-button:hover:not(:disabled) {
		background: #3a5ae7;
	}

	.submit-button:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.back-button {
		background: none;
		border: 1px solid #404040;
		border-radius: 8px;
		padding: 12px 24px;
		color: #ffffff;
		font-size: 14px;
		cursor: pointer;
		transition: all 0.2s;
		width: 100%;
	}

	.back-button:hover:not(:disabled) {
		background: #2a2a2a;
		border-color: #4a6cf7;
	}

	.error-message {
		color: #ff4444;
		font-size: 14px;
		text-align: center;
		padding: 8px;
		background: rgba(255, 68, 68, 0.1);
		border-radius: 4px;
		border: 1px solid rgba(255, 68, 68, 0.3);
	}

	.success-message {
		color: #4ade80;
		font-size: 14px;
		text-align: center;
		padding: 8px;
		background: rgba(74, 222, 128, 0.1);
		border-radius: 4px;
		border: 1px solid rgba(74, 222, 128, 0.3);
	}

	.spinner {
		width: 16px;
		height: 16px;
		border: 2px solid #ffffff;
		border-top: 2px solid transparent;
		border-radius: 50%;
		animation: spin 1s linear infinite;
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}
</style>
